import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationApi, type NotificationDelivery } from '../../services/api';

const STATUS_BADGE: Record<string, string> = {
  sent: 'bg-success',
  skipped: 'bg-secondary',
  failed: 'bg-danger',
};

/**
 * Admin → Notiser → Leveranser (ClickUp 869fkva3u): what each user was sent,
 * in the app and by e-mail, and why an e-mail did not go out.
 */
export function NotificationDeliveryLog() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [rows, setRows] = useState<NotificationDelivery[] | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(async (recipient: string) => {
    setRows(null);
    setError(false);
    try {
      setRows(await notificationApi.getDeliveries({
        ...(recipient.trim() ? { email: recipient.trim() } : {}),
        limit: 100,
      }));
    } catch {
      setRows([]);
      setError(true);
    }
  }, []);

  useEffect(() => {
    void load('');
  }, [load]);

  const time = (value: string) => new Date(value).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'sv-SE', {
    dateStyle: 'short',
    timeStyle: 'short',
  });

  return (
    <div>
      <p className="text-muted">{t('adminNotifications.deliveries.help')}</p>
      <form
        className="row g-2 align-items-end mb-3"
        onSubmit={event => {
          event.preventDefault();
          void load(email);
        }}
      >
        <div className="col-12 col-md-6">
          <label className="form-label" htmlFor="notification-delivery-email">
            {t('adminNotifications.deliveries.searchLabel')}
          </label>
          <input
            id="notification-delivery-email"
            className="form-control"
            type="email"
            inputMode="email"
            value={email}
            placeholder={t('adminNotifications.deliveries.searchPlaceholder')}
            onChange={event => setEmail(event.target.value)}
          />
        </div>
        <div className="col-12 col-md-auto">
          <button type="submit" className="btn btn-primary w-100">
            {t('adminNotifications.deliveries.search')}
          </button>
        </div>
      </form>
      {error && <div className="alert alert-danger" role="alert">{t('adminNotifications.deliveries.loadError')}</div>}
      {rows === null ? (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('adminNotifications.deliveries.loading')}</span>
          </div>
        </div>
      ) : rows.length === 0 ? (
        !error && <p className="text-muted mb-0">{t('adminNotifications.deliveries.empty')}</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th scope="col">{t('adminNotifications.deliveries.columns.time')}</th>
                <th scope="col">{t('adminNotifications.deliveries.columns.recipient')}</th>
                <th scope="col">{t('adminNotifications.deliveries.columns.notification')}</th>
                <th scope="col">{t('adminNotifications.deliveries.columns.inApp')}</th>
                <th scope="col">{t('adminNotifications.deliveries.columns.email')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.notificationId}>
                  <td className="text-nowrap">{time(row.createdAt)}</td>
                  <td>
                    <div>{row.recipient.name ?? '–'}</div>
                    <small className="text-muted text-break">{row.recipient.email ?? '–'}</small>
                  </td>
                  <td>
                    <div>{row.title}</div>
                    <small className="text-muted text-break"><code>{row.eventKey}</code></small>
                  </td>
                  <td>
                    {row.inApp
                      ? t(row.inApp.read ? 'adminNotifications.deliveries.read' : 'adminNotifications.deliveries.unread')
                      : '–'}
                  </td>
                  <td>
                    {row.email ? (
                      <>
                        <span className={`badge ${STATUS_BADGE[row.email.status] ?? 'bg-secondary'}`}>
                          {t(`adminNotifications.deliveries.status.${row.email.status}`)}
                        </span>
                        {row.email.failureReason && (
                          <small className="d-block text-muted">{row.email.failureReason}</small>
                        )}
                        {row.email.providerMessageId && (
                          <small className="d-block text-muted text-break">
                            {t('adminNotifications.deliveries.providerId', { id: row.email.providerMessageId })}
                          </small>
                        )}
                      </>
                    ) : (
                      <span className="text-muted">{t('adminNotifications.deliveries.noEmail')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default NotificationDeliveryLog;
